// Full-width Shorts-style row of Vimeo video embeds. Each card is a
// click target — clicking opens a centred lightbox with the Vimeo
// player at full vertical-portrait size, like YouTube Shorts. ESC,
// the close button, or clicking the backdrop dismisses.
//
// Slots populated by repeating an editor-managed list of Vimeo IDs
// (read from /api/site-embeds), with a hard-coded fallback if the
// fetch fails or the list is empty.

import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { FALLBACK_VIDEO_IDS } from '../../lib/embedFallbacks';
import './VideosStrip.css';

const VIMEO_PLAYER_SRC = 'https://player.vimeo.com/api/player.js';

function vimeoEmbedUrl(id: string, autoplay: boolean): string {
  const params = new URLSearchParams({
    badge: '0',
    autopause: '0',
    player_id: '0',
    app_id: '58479',
  });
  if (autoplay) {
    params.set('autoplay', '1');
    // muted=1 is required by browsers for autoplay to actually fire
    // without a user gesture; the user can unmute from the player UI.
    params.set('muted', '1');
  }
  return `https://player.vimeo.com/video/${id}?${params.toString()}`;
}

export default function VideosStrip() {
  const [activeId, setActiveId] = useState<string | null>(null);
  const [videoIds, setVideoIds] = useState<string[]>(FALLBACK_VIDEO_IDS);

  useEffect(() => {
    if (document.querySelector(`script[src="${VIMEO_PLAYER_SRC}"]`)) return;
    const script = document.createElement('script');
    script.async = true;
    script.src = VIMEO_PLAYER_SRC;
    document.body.appendChild(script);
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetch('/api/site-embeds', { cache: 'no-store' })
      .then((r) => (r.ok ? r.json() : null))
      .then((data: { embeds?: { videos?: string[] } } | null) => {
        if (cancelled) return;
        const ids = data?.embeds?.videos ?? [];
        if (ids.length > 0) setVideoIds(ids);
      })
      .catch(() => {
        // Silent — keep fallback list.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <>
      <section className="videos-strip" aria-label="Recent Lovin Malta videos">
        <div className="videos-strip__inner">
          <div className="videos-strip__rail">
            {videoIds.map((videoId, i) => (
              <button
                key={`${videoId}-${i}`}
                type="button"
                className="videos-strip__slot"
                onClick={() => setActiveId(videoId)}
                aria-label={`Play video ${i + 1}`}
              >
                <div className="videos-strip__aspect">
                  {/* Inline iframe shows the Vimeo poster + play button.
                      pointer-events: none so the wrapping <button> handles
                      the click and routes it to the modal. */}
                  <iframe
                    src={vimeoEmbedUrl(videoId, false)}
                    frameBorder={0}
                    allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
                    referrerPolicy="strict-origin-when-cross-origin"
                    title={`Lovin Malta clip ${i + 1} — preview`}
                    tabIndex={-1}
                  />
                  <span className="videos-strip__play" aria-hidden="true">
                    ▶
                  </span>
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>
      {activeId && (
        <VideoViewer videoId={activeId} onClose={() => setActiveId(null)} />
      )}
    </>
  );
}

function VideoViewer({
  videoId,
  onClose,
}: {
  videoId: string;
  onClose: () => void;
}) {
  // Lock background scroll while the modal is open.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  // ESC dismisses.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return createPortal(
    <div
      className="video-viewer"
      role="dialog"
      aria-modal="true"
      aria-label="Video player"
      onClick={onClose}
    >
      <button
        type="button"
        className="video-viewer__close"
        onClick={onClose}
        aria-label="Close video"
      >
        ×
      </button>
      <div
        className="video-viewer__frame"
        onClick={(e) => e.stopPropagation()}
      >
        <iframe
          src={vimeoEmbedUrl(videoId, true)}
          frameBorder={0}
          allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
          referrerPolicy="strict-origin-when-cross-origin"
          title="Lovin Malta clip"
          allowFullScreen
        />
      </div>
    </div>,
    document.body,
  );
}
